import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { staffService } from '../../services/staffService';
import { workOrderService } from '../../services/workOrderService';
import { 
  FiArrowLeft, FiUser, FiPhone, FiMail, FiBriefcase, FiClock, 
  FiCalendar, FiPackage, FiCheckCircle, FiXCircle, FiRefreshCw 
} from 'react-icons/fi';

const StaffDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadData();
  }, [id, refreshTrigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const staffData = await staffService.getById(id);
      if (staffData) {
        setStaff(staffData);
        const allWO = await workOrderService.getAll();
        const staffWO = allWO.filter(wo => wo.staffId === id);
        setWorkOrders(staffWO);
      }
    } catch (error) {
      console.error('Error loading staff detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStaffStatusBadge = (status) => {
    const map = {
      'active': { label: 'Active', color: 'bg-green-100 text-green-800' },
      'inactive': { label: 'Inactive', color: 'bg-red-100 text-red-800' },
      'on-leave': { label: 'On Leave', color: 'bg-yellow-100 text-yellow-800' }
    };
    return map[status] || map['active'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading...</p>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-600">❌ Staff tidak ditemukan</p>
      </div>
    );
  }

  const statusBadge = getStaffStatusBadge(staff.status);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/staff')}
          className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
        >
          <FiArrowLeft size={24} className="text-[var(--text-primary)]" />
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Staff Detail</h1>
        <button
          onClick={refreshData}
          className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors ml-auto"
          title="Refresh data"
        >
          <FiRefreshCw size={20} className="text-blue-500" />
        </button>
      </div>

      {/* Staff Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
            
            {/* FOTO -DiAtas */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex-shrink-0">
                {staff.photo ? (
                  <img
                    src={staff.photo}
                    alt={staff.name}
                    className="w-17 h-17 rounded-full object-cover border-2 border-[var(--border-color)]"
                  />
                ) : (
                  <div className="w-17 h-17 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-2xl border-2 border-[var(--border-color)]">
                    {staff.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-xl font-bold text-[var(--text-primary)]">{staff.name}</h2>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadge.color}`}>
                    {statusBadge.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Detail Lainnya */}
            <div className="mt-4 space-y-3">
              <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                <FiPhone className="text-green-500" />
                <span>{staff.phone}</span>
              </p>
              {staff.email && (
                <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                  <FiMail className="text-purple-500" />
                  <span>{staff.email}</span>
                </p>
              )}
              <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                <FiBriefcase className="text-blue-500" />
                <span>Level: <strong>{staff.level}</strong></span>
              </p>
              <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                <FiClock className="text-orange-500" />
                <span>Commission: <strong>{staff.commission}%</strong></span>
              </p>
              {staff.joinDate && (
                <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                  <FiCalendar className="text-red-500" />
                  <span>Join: {new Date(staff.joinDate).toLocaleDateString('id-ID')}</span>
                </p>
              )}
            </div>

            {/* Skills */}
            {staff.skills && staff.skills.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                <p className="text-sm font-medium text-[var(--text-secondary)] mb-2">Skills</p>
                <div className="flex flex-wrap gap-1">
                  {staff.skills.map((skill, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {staff.notes && (
              <div className="mt-4 p-3 bg-[var(--bg-secondary)] rounded-lg">
                <p className="text-sm font-medium text-[var(--text-secondary)]">Notes</p>
                <p className="text-sm text-[var(--text-primary)]">{staff.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Work Orders */}
        <div className="lg:col-span-2">
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center space-x-2">
              <FiPackage size={20} />
              <span>Work Orders ({workOrders.length})</span>
            </h3>

            {workOrders.length === 0 ? (
              <p className="text-[var(--text-secondary)] text-center py-8">Belum ada work order untuk staff ini</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-secondary)]">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">WO Number</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Project</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Target</th>
                      <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrders.map((wo) => (
                      <tr key={wo.id} className="border-t border-[var(--border-color)]">
                        <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{wo.woNumber}</td>
                        <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{wo.projectName}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(wo.status)}`}>
                            {wo.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">
                          {wo.targetDate || '-'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => navigate(`/work-orders/${wo.id}`)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDetail;